import { motion, AnimatePresence } from "framer-motion";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { MINT_START_DATE } from "@/config/mint";

const personalities = [
  {
    id: "reaper",
    name: "The Reaper",
    description: "When The Reaper enters the chat, it's game over for bullish dreams. Known for bringing doom to even the most hyped tokens, he thrives in the shadows of red charts and market crashes.",
    imagePath: "reaper",
    color: "from-red-500 to-purple-500"
  },
  {
    id: "altcoin-addict",
    name: "Altcoin Addict",
    description: "This Cryptfluencer treats low-cap gems like potato chips—he just can't stop at one! He's got a portfolio that's wilder than a meme coin chart and riskier than trying to explain crypto to your grandma.",
    imagePath: "addict",
    color: "from-green-500 to-blue-500"
  },
  {
    id: "token-trickster",
    name: "Token Trickster",
    description: "Master of the \"too good to be true\" ICO, this Cryptfluencer can spin up a new coin faster than you can say \"pump and dump.\" With a flashy whitepaper full of buzzwords and zero substance.",
    imagePath: "trickster",
    color: "from-yellow-500 to-red-500"
  },
  {
    id: "market-mover",
    name: "Market Mover",
    description: "This Cryptfluencer's tweets have more impact than actual blockchain technology! With a single emoji, he can pump a coin to new heights—before sending it crashing down harder than a rug-pulled meme token.",
    imagePath: "market",
    color: "from-blue-500 to-purple-500"
  },
  {
    id: "luxury-lord",
    name: "Luxury Lord",
    description: "He's not just living the high life; he's posting about it every five minutes! From Lambos to private jets, this Cryptfluencer is more focused on flexing than fundamentals.",
    imagePath: "luxury",
    color: "from-yellow-500 to-orange-500"
  },
  {
    id: "crypto-houdini",
    name: "Crypto Houdini",
    description: "Watch in awe as this Cryptfluencer performs the ultimate vanishing act! One minute he's promising life-changing gains, and the next… poof, he's gone—along with your investment.",
    imagePath: "houdini",
    color: "from-purple-500 to-pink-500"
  },
  {
    id: "algorithm-assassin",
    name: "Algorithm Assassin",
    description: "This Cryptfluencer slices through charts like a ninja in a bull market. He's got a black belt in TA (Technical Analysis) and a knack for predicting the moon… right before it crashes back to Earth.",
    imagePath: "assasin",
    color: "from-red-500 to-orange-500"
  }
];

export function PersonalitiesCarousel() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(0);
  const [isRevealed, setIsRevealed] = useState(false);

  useEffect(() => {
    // Check if we're already past reveal time on mount
    const currentTime = new Date();
    const mintStartDate = new Date(MINT_START_DATE);
    if (currentTime >= mintStartDate) {
      setIsRevealed(true);
    }

    // Listen for reveal event
    const handleReveal = (event: CustomEvent<{ revealed: boolean }>) => {
      setIsRevealed(event.detail.revealed);
    };

    window.addEventListener('nftReveal', handleReveal as EventListener);

    return () => {
      window.removeEventListener('nftReveal', handleReveal as EventListener);
    };
  }, []);

  const slideVariants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 1000 : -1000,
      opacity: 0
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1
    },
    exit: (direction: number) => ({
      zIndex: 0,
      x: direction < 0 ? 1000 : -1000,
      opacity: 0
    })
  };

  const swipeConfidenceThreshold = 10000;
  const swipePower = (offset: number, velocity: number) => {
    return Math.abs(offset) * velocity;
  };

  const paginate = (newDirection: number) => {
    setDirection(newDirection);
    setCurrentIndex((prevIndex) => {
      let newIndex = prevIndex + newDirection;
      if (newIndex < 0) newIndex = personalities.length - 1;
      if (newIndex >= personalities.length) newIndex = 0;
      return newIndex;
    });
  };

  useEffect(() => {
    const timer = setInterval(() => {
      paginate(1);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  return (
    <section className="relative py-20 overflow-hidden bg-black/40">
      <div className="absolute inset-0 bg-[url('/grid.svg')] bg-center [mask-image:linear-gradient(180deg,white,rgba(255,255,255,0))] opacity-20" />
      
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="text-center max-w-4xl mx-auto mb-12"
        >
          <h2 className="text-4xl font-bold mb-6 text-[#9333EA]">Meet the Cryptfluencers</h2>
          <p className="text-sm md:text-base text-nft-muted mb-8">
            A collection of the most notorious characters in the crypto space, each with their own unique story and traits.
          </p>
        </motion.div>

        <div className="relative h-[400px] max-w-2xl mx-auto">
          <AnimatePresence initial={false} custom={direction}>
            <motion.div
              key={currentIndex}
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{
                x: { type: "spring", stiffness: 300, damping: 30 },
                opacity: { duration: 0.2 }
              }}
              drag="x"
              dragConstraints={{ left: 0, right: 0 }}
              dragElastic={1}
              onDragEnd={(e, { offset, velocity }) => {
                const swipe = swipePower(offset.x, velocity.x);
                if (swipe < -swipeConfidenceThreshold) {
                  paginate(1);
                } else if (swipe > swipeConfidenceThreshold) {
                  paginate(-1);
                }
              }}
              className="absolute w-full"
            >
              <div className="bg-[#0a0a0a] border border-[#222] rounded-lg overflow-hidden p-8">
                <div className="flex items-center justify-center mb-6">
                  <div className="w-32 h-32 rounded-full overflow-hidden">
                    <img
                      src={`/brand-images/personalities/${isRevealed ? 'revealed' : 'not-revealed'}/${personalities[currentIndex].imagePath}.png`}
                      alt={personalities[currentIndex].name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
                
                <h2 className={`text-xl font-pixel text-center mb-6 bg-gradient-to-r ${personalities[currentIndex].color} bg-clip-text text-transparent`}>
                  {personalities[currentIndex].name}
                </h2>
                
                <p className="text-nft-muted text-sm leading-relaxed text-center">
                  {personalities[currentIndex].description}
                </p>
              </div>
            </motion.div>
          </AnimatePresence>

          <div className="absolute left-0 right-0 bottom-[-60px] flex justify-center gap-2">
            {personalities.map((_, index) => (
              <button
                key={index}
                onClick={() => {
                  const direction = index > currentIndex ? 1 : -1;
                  setDirection(direction);
                  setCurrentIndex(index);
                }}
                className={`w-2 h-2 rounded-full transition-all ${
                  index === currentIndex ? "bg-[#9333EA] w-4" : "bg-white/20"
                }`}
              />
            ))}
          </div>

          <Button
            onClick={() => paginate(-1)}
            className="absolute left-[-60px] top-1/2 transform -translate-y-1/2 bg-[#9333EA] hover:bg-[#9333EA]/80 text-white rounded-full p-3"
          >
            ←
          </Button>
          <Button
            onClick={() => paginate(1)}
            className="absolute right-[-60px] top-1/2 transform -translate-y-1/2 bg-[#9333EA] hover:bg-[#9333EA]/80 text-white rounded-full p-3"
          >
            →
          </Button>
        </div>
      </div>
    </section>
  );
} 